import type { PageSize, UUID } from '../../model/types';

export interface PageBox {
  pageId: UUID;
  /** Top edge in document units (pages stacked vertically). */
  top: number;
  /** Left edge in document units (narrower pages are centred). */
  left: number;
  size: PageSize;
}

const GAP = 24;
const SIDE_MARGIN_PX = 12;
/** Zoom limits, relative to fit-width. */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 5;

/**
 * Document layout and view state. Document units equal page units.
 * zoom = fitZoom · userZoom (CSS px per unit). Page origins are snapped to
 * whole device pixels so tiles, live ink and captured coordinates agree.
 */
export class Viewport {
  width = 1;
  height = 1;
  dpr = 1;
  zoom = 1;
  /** Zoom relative to fit-width (1 = page fills the width). */
  userZoom = 1;
  /** Document x at the left edge of the viewport. */
  scrollX = 0;
  /** Document y at the top edge of the viewport. */
  scrollY = 0;
  pages: PageBox[] = [];
  docHeight = 0;
  contentWidth = 1;

  setSize(width: number, height: number, dpr: number): void {
    // Keep the document point at the top-centre of the view fixed across resizes.
    const anchorX = this.scrollX + this.width / 2 / this.zoom;
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.dpr = dpr;
    this.zoom = this.fitZoom * this.userZoom;
    this.scrollX = anchorX - this.width / 2 / this.zoom;
    this.clamp();
  }

  setPages(pages: { id: UUID; size: PageSize }[]): void {
    this.contentWidth = Math.max(1, ...pages.map((p) => p.size.width));
    let y = GAP;
    this.pages = pages.map((p) => {
      const box = { pageId: p.id, top: y, left: (this.contentWidth - p.size.width) / 2, size: p.size };
      y += p.size.height + GAP;
      return box;
    });
    this.docHeight = y;
    this.setSize(this.width, this.height, this.dpr);
  }

  get fitZoom(): number {
    return Math.max(0.05, (this.width - 2 * SIDE_MARGIN_PX) / this.contentWidth);
  }

  /** Device-pixel scale of one page unit. */
  get scale(): number {
    return this.zoom * this.dpr;
  }

  /** Page origin in whole device pixels, relative to the viewport's top-left. */
  originDev(i: number): { x: number; y: number } {
    const p = this.pages[i]!;
    return {
      x: Math.round((p.left - this.scrollX) * this.scale),
      y: Math.round((p.top - this.scrollY) * this.scale),
    };
  }

  /** Page origin in CSS px relative to the viewport. */
  originCss(i: number): { x: number; y: number } {
    const o = this.originDev(i);
    return { x: o.x / this.dpr, y: o.y / this.dpr };
  }

  /** Index of the page under (or nearest to) a viewport-relative CSS point. */
  pageAt(_x: number, y: number): number {
    if (this.pages.length === 0) return -1;
    const docY = this.scrollY + y / this.zoom;
    let best = 0, bestDist = Infinity;
    this.pages.forEach((p, i) => {
      const d = docY < p.top ? p.top - docY : docY > p.top + p.size.height ? docY - p.top - p.size.height : 0;
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  }

  /** Indices of pages intersecting the viewport (plus `marginPx` CSS px above/below). */
  visiblePages(marginPx = 0): number[] {
    const top = this.scrollY - marginPx / this.zoom;
    const bottom = this.scrollY + (this.height + marginPx) / this.zoom;
    const out: number[] = [];
    this.pages.forEach((p, i) => {
      if (p.top + p.size.height >= top && p.top <= bottom) out.push(i);
    });
    return out;
  }

  get maxScroll(): number {
    return Math.max(0, this.docHeight - this.height / this.zoom);
  }

  /** Scrolls by CSS px. Returns true if the view moved. */
  scrollBy(dyCss: number): boolean {
    return this.panBy(0, dyCss);
  }

  /** Pans by CSS px in both axes. Returns true if the view moved. */
  panBy(dxCss: number, dyCss: number): boolean {
    const bx = this.scrollX, by = this.scrollY;
    this.scrollX += dxCss / this.zoom;
    this.scrollY += dyCss / this.zoom;
    this.clamp();
    return this.scrollX !== bx || this.scrollY !== by;
  }

  scrollTo(docY: number): void {
    this.scrollY = docY;
    this.clamp();
  }

  /**
   * Multiplies the zoom by `factor`, keeping the document point under the
   * viewport-relative CSS point (cx, cy) fixed. Returns true if zoom changed.
   */
  zoomAt(factor: number, cx: number, cy: number): boolean {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.userZoom * factor));
    if (next === this.userZoom) return false;
    const docX = this.scrollX + cx / this.zoom;
    const docY = this.scrollY + cy / this.zoom;
    this.userZoom = next;
    this.zoom = this.fitZoom * next;
    this.scrollX = docX - cx / this.zoom;
    this.scrollY = docY - cy / this.zoom;
    this.clamp();
    return true;
  }

  /** Back to fit-width, keeping the vertical centre of the view in place. */
  resetZoom(): void {
    const midY = this.scrollY + this.height / 2 / this.zoom;
    this.userZoom = 1;
    this.zoom = this.fitZoom;
    this.scrollY = midY - this.height / 2 / this.zoom;
    this.clamp();
  }

  private clamp(): void {
    const viewW = this.width / this.zoom;
    const m = SIDE_MARGIN_PX / this.zoom;
    if (this.contentWidth + 2 * m <= viewW) {
      this.scrollX = (this.contentWidth - viewW) / 2; // centred
    } else {
      this.scrollX = Math.min(Math.max(-m, this.scrollX), this.contentWidth + m - viewW);
    }
    this.scrollY = Math.min(Math.max(0, this.scrollY), this.maxScroll);
  }
}
